<template>
  <SettingsPagePanel>
    <header :class="$style.header">
      <p :class="[$style.header__title, $style.title]">
        アプローチ自動代行条件 - {{ conditionText }}
      </p>
    </header>

    <section :class="$style.content" v-if="setting && form">
      <div :class="[$style.content__option, $style.option]">
        <p :class="[$style.option__text, $style.text]">タイトル</p>
        <DolphinTextInput
          v-model="form.title"
          :invalid="validationResult('title').invalid && isClickedSave"
        />
        <ErrorMessageHandler
          :error="
            validationResult('title').invalid && isClickedSave
              ? validationResult('title').errorText
              : undefined
          "
        />
      </div>

      <div :class="[$style.content__option, $style.option]">
        <p :class="[$style.option__text, $style.text]">ステータス</p>
        <ul :class="$style.radiobuttonSection">
          <li v-for="(option, index) in booleanOptions" :key="index">
            <DolphinRadioButtonInContainer
              :class="$style.radiobutton"
              name="isEnabled"
              v-model="form.isEnabled"
              :value="option.value"
            >
              {{ option.label }}
            </DolphinRadioButtonInContainer>
          </li>
        </ul>
      </div>

      <div
        :class="[$style.content__option, $style.option]"
        v-if="entranceOptions"
      >
        <p :class="[$style.option__text, $style.text]">
          このアプローチを送信するプランナー
        </p>
        <DolphinSelector
          v-model="form.entranceGuid"
          :options="entranceOptions"
          :invalid="validationResult('entranceGuid').invalid && isClickedSave"
        />
        <ErrorMessageHandler
          :error="
            validationResult('entranceGuid').invalid && isClickedSave
              ? validationResult('entranceGuid').errorText
              : undefined
          "
        />
      </div>

      <div :class="[$style.content__option, $style.option]">
        <p :class="[$style.option__text, $style.text]">メッセージ内容</p>
        <DolphinTextarea
          v-model="form.body"
          :rows="12"
          :invalid="validationResult('body').invalid && isClickedSave"
          :class="$style.textArea"
        />
        <ErrorMessageHandler
          :error="
            validationResult('body').invalid && isClickedSave
              ? validationResult('body').errorText
              : undefined
          "
        />
      </div>

      <div :class="[$style.content__option, $style.option]">
        <MessageAttachmentActions
          v-model="form.selections"
          :invalid="validationResult('selections').invalid && isClickedSave"
          :isDeleteAllSelectionsAllowed="false"
        />
        <ErrorMessageHandler
          :error="
            validationResult('selections').invalid && isClickedSave
              ? validationResult('selections').errorText
              : undefined
          "
        />
      </div>

      <SettingsPageAttackListSenderEditor
        :class="[$style.content__editor, $style.editor]"
        v-model="editorForm"
      />
    </section>

    <footer :class="$style.footer">
      <FlatButton
        v-if="isUpdateView"
        :class="$style.footer__delete"
        @click="emit('delete')"
        variant="caution"
        >削除</FlatButton
      >
      <div :class="[$style.buttonSection, $style.footer__buttonSection]">
        <FlatButton @click="emit('cancel')" variant="secondary"
          >キャンセル</FlatButton
        >
        <FlatButton @click="handleSave">保存</FlatButton>
      </div>
    </footer>
  </SettingsPagePanel>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

import {
  booleanOptions,
  createEntranceOptions,
  type Range,
} from './SettingsPageAttackListSenderEditorOptions'

import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import DolphinTextarea from '@/components/atoms/DolphinTextarea.vue'
import ErrorMessageHandler from '@/components/atoms/ErrorMessageHandler.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import DolphinRadioButtonInContainer from '@/components/molecules/DolphinRadioButtonInContainer.vue'
import MessageAttachmentActions from '@/components/organisms/MessageAttachmentActions/MessageAttachmentActions.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import SettingsPageAttackListSenderEditor from './SettingsPageAttackListSenderEditor.vue'

import type { SettingsPageAttackListSenderEditViewEntranceFragment } from './SettingsPageAttackListSenderEditView.generated'
import {
  AttackListSenderSettingRuleExistence,
  AttackListSenderSettingRuleGender,
} from '@/types/schema.generated'
import { useAlert } from '@/libs/alertPortal'
import { useMaybeRoutes } from '@/features/router/route'

const props = defineProps<{
  setting: FormData
  entrances: SettingsPageAttackListSenderEditViewEntranceFragment[]
}>()

const maybeRoute = useMaybeRoutes([
  'attackListSenderSettingNew',
  'attackListSenderSettingUpdate',
])

const isUpdateView = computed(() => {
  if (maybeRoute.value == undefined) {
    return false
  }

  return maybeRoute.value.name === 'attackListSenderSettingUpdate'
})

const conditionText = computed(() => {
  if (maybeRoute.value == undefined) {
    return ''
  }

  return maybeRoute.value.name === 'attackListSenderSettingNew'
    ? '新規作成'
    : '編集'
})

const entranceOptions = computed(() =>
  createEntranceOptions(props.entrances ?? [])
)

export type FormData = {
  title: string
  isEnabled: boolean
  entranceGuid: Maybe<string>
  body: string
  selections: string[]
  age: Range
  gender: AttackListSenderSettingRuleGender
  saving: Range
  annualIncome: Range
  investment: Range
  hasPartner: AttackListSenderSettingRuleExistence
  familyAnnualIncome: Range
  hasChild: AttackListSenderSettingRuleExistence
  ageOfFirstChild: Range
  shouldSendIfSameCorporationApproached: boolean
}

const form = ref({
  title: props.setting.title,
  isEnabled: props.setting.isEnabled,
  entranceGuid: props.setting.entranceGuid,
  body: props.setting.body,
  selections: props.setting.selections,
})

const editorForm = ref({
  age: props.setting.age,
  gender: props.setting.gender,
  saving: props.setting.saving,
  annualIncome: props.setting.annualIncome,
  investment: props.setting.investment,
  hasPartner: props.setting.hasPartner,
  familyAnnualIncome: props.setting.familyAnnualIncome,
  hasChild: props.setting.hasChild,
  ageOfFirstChild: props.setting.ageOfFirstChild,
  shouldSendIfSameCorporationApproached:
    props.setting.shouldSendIfSameCorporationApproached,
})

const emit = defineEmits<{
  (event: 'save', form: FormData): void
  (event: 'cancel'): void
  (event: 'delete'): void
}>()

// 初回表示時にエラーにしないためのフラグ
const isClickedSave = ref(false)
const alert = useAlert()

const handleSave = () => {
  isClickedSave.value = true

  if (formValidationErrors.value.some(error => error.invalid)) {
    alert.error('入力内容を修正してください')
  } else {
    emit('save', { ...form.value, ...editorForm.value })
  }
}

const formValidationErrors = computed(() => [
  {
    key: 'title',
    invalid: form.value.title === '',
    errorText: 'タイトルを入力してください',
  },
  {
    key: 'body',
    invalid: form.value.body === '',
    errorText: 'メッセージ内容を入力してください',
  },
  {
    key: 'selections',
    invalid:
      form.value.selections.filter(selection => selection !== '').length < 2,
    errorText: '選択肢は2つ以上入力してください',
  },
  {
    key: 'entranceGuid',
    invalid: !form.value.entranceGuid,
    errorText: 'プランナーを選択してください',
  },
])

const validationResult = (key: string) =>
  formValidationErrors.value.filter(error => error.key === key)[0]
</script>

<style lang="scss" module>
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.title {
  font-size: 20px;
  font-weight: bold;
}

.content {
  border-top: 1px solid $color-border-low-emphasis;
  padding: 16px 0;

  &:nth-child(n + 2) {
    margin-top: 20px;
  }
}

.option {
  display: flex;
  flex-direction: column;
  gap: 8px;

  &:nth-child(n + 2) {
    margin-top: 28px;
  }
}

.radiobuttonSection {
  display: flex;
  gap: 8px;
}

.radiobutton {
  width: 104px;
}

.text {
  font-weight: 700;
}

.editor {
  max-width: 100%;

  &:nth-child(n + 2) {
    margin-top: 32px;
  }
}

.footer {
  padding-top: 16px;
  border-top: 1px solid $color-border-low-emphasis;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;

  &__delete {
    justify-self: start;
  }

  &__buttonSection {
    justify-self: end;
    grid-column: 3;
  }
}

.buttonSection {
  display: flex;
  gap: 16px;
}

.textArea {
  resize: vertical;
}
</style>
